/**
 * SPACE MODE (docs/01 §9.1) — one tap: work / chill / gaming.
 *
 * The clock cannot tell whether the place is currently an office or a games
 * room: someone here at 3am might be working or might be on the PlayStation. So
 * the mode is explicit, and it is a scoring input for the wall board rather than
 * a filter.
 *
 * Two rules are enforced by this component's shape, not by a comment:
 *
 *  - **`empty` is inferred, never set.** It is derived from presence — nobody
 *    detected and nobody checked in — so there is no button for it, and the
 *    handler's type does not admit it.
 *  - **Mode never auto-resets.** It persists until a human changes it. When the
 *    space empties, the chosen mode is still what comes back, which is why the
 *    empty banner names the mode it will return to instead of implying it was
 *    lost.
 */

import {
  SpaceStyles,
  formatAgo,
  isSpaceEmpty,
  mutationCtx,
  nameLookup,
  useAction,
  useNow,
  useSpace,
  type SpaceScreenProps,
} from './spaceData.js'
import { mutations } from '../../lib/mutations.js'
import type { SpaceModeValue } from '@office/domain'

/** The modes a human may set. `empty` is deliberately absent. */
export type SettableMode = 'work' | 'chill' | 'gaming'

export const SETTABLE_MODES: readonly { key: SettableMode; label: string; hint: string }[] = [
  { key: 'work', label: 'Work', hint: 'Chores and tasks rise, games fall' },
  { key: 'chill', label: 'Chill', hint: 'Notes, music and the lunch decider rise' },
  { key: 'gaming', label: 'Gaming', hint: 'Queue and ladder rise, chores fall' },
]

export interface SpaceModeProps extends SpaceScreenProps {
  readonly tickMs?: number
}

export function SpaceModeSwitch(props: SpaceModeProps) {
  const { db, clock } = props
  const now = useNow(clock, props.tickMs ?? 60_000)
  const space = useSpace(db)
  const run = useAction()
  const nameOf = nameLookup(space.people)

  const empty = isSpaceEmpty(space.presenceEvents, now, space.settings.dayRolloverHour)
  const stored = space.spaceMode.current
  const effective: SpaceModeValue = empty ? 'empty' : stored
  const chosen: SettableMode = stored === 'empty' ? 'work' : stored

  const set = (mode: SettableMode) =>
    run(() => mutations.setSpaceMode(mutationCtx(props), mode))

  return (
    <section className="sp" aria-labelledby="sp-mode-h">
      <SpaceStyles />
      <div className="sp-head sp-head-plain">
        <h2 className="sp-title" id="sp-mode-h">
          Space mode
        </h2>
        <span className="sp-note" data-testid="space-mode-current">
          {labelFor(effective)}
        </span>
      </div>

      <div className="sp-seg" role="group" aria-label="Space mode">
        {SETTABLE_MODES.map(mode => (
          <button
            key={mode.key}
            type="button"
            aria-pressed={!empty && chosen === mode.key}
            onClick={() => set(mode.key)}
          >
            {mode.label}
          </button>
        ))}
      </div>

      {empty ? (
        <p className="sp-banner" data-tone="info" role="status">
          The space is empty — inferred from presence, never set by hand. It returns to{' '}
          {labelFor(chosen)} when someone checks in.
        </p>
      ) : (
        <p className="sp-note">{SETTABLE_MODES.find(m => m.key === chosen)?.hint ?? ''}</p>
      )}

      <p className="sp-note">
        {space.spaceMode.setAt === null
          ? 'Mode has not been set yet.'
          : `Set ${
              space.spaceMode.setBy === null || space.spaceMode.auto
                ? 'automatically'
                : `by ${nameOf(space.spaceMode.setBy)}`
            } ${formatAgo(space.spaceMode.setAt, now)}.`}{' '}
        It never resets on its own.
      </p>
    </section>
  )
}

export function labelFor(mode: SpaceModeValue): string {
  switch (mode) {
    case 'work':
      return 'Work'
    case 'chill':
      return 'Chill'
    case 'gaming':
      return 'Gaming'
    case 'empty':
      return 'Empty'
  }
}
